import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { 
    IconReport, IconBriefcase, IconPrinter, IconQuote, IconTrendingUp, IconTrendingDown 
} from '../components/Icons';
import toast from 'react-hot-toast';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

interface ReportData {
    generated_at: string;
    nav: number;
    pnl_abs: number;
    pnl_pct: number;
    positions_count: number;
    top_positions: any[];
    ai_commentary: string;
}

const ReportingView: React.FC = () => {
    const { currentUser } = useAuth();
    const [books, setBooks] = useState<any[]>([]);
    const [selectedBook, setSelectedBook] = useState<string>('');
    const [report, setReport] = useState<ReportData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // 1. Fetch Books
    useEffect(() => {
        const loadBooks = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/portfolios`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.portfolios) {
                    setBooks(data.portfolios);
                    if (data.portfolios.length > 0) setSelectedBook(data.portfolios[0].id);
                }
            } catch (e) {
                console.error(e);
            }
        };
        loadBooks();
    }, [currentUser]);

    // 2. Generate Report
    const handleGenerate = async () => {
        if (!selectedBook || !currentUser) return;
        setIsLoading(true);
        setReport(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/reporting/generate`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ portfolio_id: selectedBook })
            });
            
            if (!res.ok) throw new Error("Report generation failed");
            
            const data = await res.json();
            setReport(data);
            toast.success("Report Generated");
        } catch (e) {
            toast.error("Could not generate report");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    return (
        <div className="h-full flex flex-col bg-slate-50 font-sans overflow-hidden">
            
            {/* TOOLBAR (Hidden when printing) */}
            <div className="p-6 bg-white border-b border-slate-200 flex justify-between items-center print:hidden shrink-0">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <IconReport className="text-purple-600"/> Reporting Center
                    </h2>
                    <div className="h-6 w-px bg-slate-200"></div>
                    <div className="relative">
                        <select 
                            value={selectedBook}
                            onChange={(e) => setSelectedBook(e.target.value)}
                            className="pl-8 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                        >
                            {books.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                        <IconBriefcase className="absolute left-2.5 top-2.5 text-slate-400" size={14}/>
                    </div>
                    <button 
                        onClick={handleGenerate}
                        disabled={isLoading}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition-colors disabled:opacity-50"
                    >
                        {isLoading ? "Generating..." : "Generate Tear Sheet"}
                    </button>
                </div>
                
                {report && (
                    <button 
                        onClick={handlePrint}
                        className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors"
                    >
                        <IconPrinter size={18} /> Print PDF
                    </button>
                )}
            </div>

            {/* REPORT CANVAS */}
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                {report ? (
                    <div className="max-w-4xl mx-auto bg-white shadow-lg p-10 print:shadow-none print:p-0">
                        {/* HEADER */}
                        <div className="flex justify-between items-start border-b border-slate-800 pb-6 mb-8">
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900 mb-1">Fund Performance Update</h1>
                                <p className="text-slate-500 text-sm">Generated: {report.generated_at}</p>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-mono font-bold text-slate-900">AiQ Asset Management</div>
                                <div className="text-xs text-slate-400 uppercase tracking-widest">Confidential</div>
                            </div>
                        </div>

                        {/* KPIS */}
                        <div className="grid grid-cols-3 gap-6 mb-10">
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="text-xs font-bold text-slate-400 uppercase mb-1">Net Asset Value</div>
                                <div className="text-2xl font-mono font-bold text-slate-800">
                                    ${report.nav.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="text-xs font-bold text-slate-400 uppercase mb-1">Total P&L</div>
                                <div className={`text-2xl font-mono font-bold ${report.pnl_abs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {report.pnl_abs >= 0 ? '+' : ''}${report.pnl_abs.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="text-xs font-bold text-slate-400 uppercase mb-1">Return %</div>
                                <div className={`text-2xl font-mono font-bold ${report.pnl_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {report.pnl_pct >= 0 ? '+' : ''}{report.pnl_pct.toFixed(2)}%
                                </div>
                            </div>
                        </div>

                        {/* TWO COLUMN LAYOUT */}
                        <div className="flex gap-8 mb-10">
                            
                            {/* LEFT: COMMENTARY */}
                            <div className="flex-1">
                                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-b border-slate-200 pb-2 mb-4 flex items-center gap-2">
                                    <IconQuote size={16}/> Manager Commentary
                                </h3>
                                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line text-justify">
                                    {report.ai_commentary}
                                </div>
                            </div>

                            {/* RIGHT: ALLOCATION PIE */}
                            <div className="w-64 shrink-0">
                                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-b border-slate-200 pb-2 mb-4">
                                    Top Allocation
                                </h3>
                                <div className="h-48 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={report.top_positions}
                                                cx="50%" cy="50%"
                                                innerRadius={40} outerRadius={70}
                                                paddingAngle={5}
                                                dataKey="market_value"
                                            >
                                                {report.top_positions.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(val: number) => `$${val.toLocaleString()}`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* HOLDINGS TABLE */}
                        <div>
                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-b border-slate-200 pb-2 mb-4">
                                Top Contributors / Detractors
                            </h3>
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                                    <tr>
                                        <th className="px-4 py-2 rounded-l">Ticker</th>
                                        <th className="px-4 py-2 text-right">Exposure</th>
                                        <th className="px-4 py-2 text-right">Mkt Value</th>
                                        <th className="px-4 py-2 text-right rounded-r">Total P&L</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {report.top_positions.map((pos) => (
                                        <tr key={pos.ticker}>
                                            <td className="px-4 py-3 font-bold text-slate-700">{pos.ticker}</td>
                                            <td className="px-4 py-3 text-right font-mono text-slate-600">{pos.shares_held.toFixed(2)}</td>
                                            <td className="px-4 py-3 text-right font-mono text-slate-600">${pos.market_value.toLocaleString()}</td>
                                            <td className={`px-4 py-3 text-right font-mono font-bold ${pos.total_pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {pos.total_pnl >= 0 ? '+' : ''}${pos.total_pnl.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center flex-col text-slate-400">
                        <IconReport size={64} className="opacity-20 mb-4" />
                        <p>Select a book and click Generate to create a Tear Sheet.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportingView;